import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AuthLayout } from '@/layouts/AuthLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { lazy, Suspense } from 'react';

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterParentPage = lazy(() => import('@/pages/auth/RegisterParentPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const StaffPage = lazy(() => import('@/pages/staff/StaffPage'));
const TransportPage = lazy(() => import('@/pages/transport/TransportPage'));
const TransportDriverPage = lazy(() => import('@/pages/transport/TransportDriverPage'));
const AccessPage = lazy(() => import('@/pages/access/AccessPage'));
const AccessGuardPage = lazy(() => import('@/pages/access/AccessGuardPage'));
const BiometricDevicesPage = lazy(() => import('@/pages/biometrics/BiometricDevicesPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const StudentsPage = lazy(() => import('@/pages/students/StudentsPage'));
const ParentsPage = lazy(() => import('@/pages/parents/ParentsPage'));
const InstitutionsPage = lazy(() => import('@/pages/institutions/InstitutionsPage'));
const TransactionsPage = lazy(() => import('@/pages/transactions/TransactionsPage'));
const WalletPage = lazy(() => import('@/pages/wallets/WalletPage'));
const MyPaymentsPage = lazy(() => import('@/pages/payments/MyPaymentsPage'));
const AdminPaymentsPage = lazy(() => import('@/pages/payments/AdminPaymentsPage'));
const CafeteriasPage = lazy(() => import('@/pages/cafeterias/CafeteriasPage'));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const MyQRPage = lazy(() => import('@/pages/qr/MyQRPage'));
const CafeteriaOperatorPage = lazy(() => import('@/pages/cafeteria/CafeteriaOperatorPage'));
const CafeteriaMenuPage = lazy(() => import('@/pages/cafeteria/CafeteriaMenuPage'));
const CafeteriaVentasPage = lazy(() => import('@/pages/cafeteria/CafeteriaVentasPage'));
const CafeteriaInventoryPage = lazy(() => import('@/pages/cafeteria/CafeteriaInventoryPage'));
const CafeteriaDashboardPage = lazy(() => import('@/pages/cafeteria/CafeteriaDashboardPage'));
const InstitutionAdminsPage = lazy(() => import('@/pages/institution-admins/InstitutionAdminsPage'));
const ParentPreOrdersPage = lazy(() => import('@/pages/pre-orders/ParentPreOrdersPage'));
const MyGroupsPage = lazy(() => import('@/pages/pre-orders/MyGroupsPage'));
const KitchenManifestPage = lazy(() => import('@/pages/pre-orders/KitchenManifestPage'));
const TeacherGroupsAdminPage = lazy(() => import('@/pages/pre-orders/TeacherGroupsAdminPage'));

const Loading = () => (
  <div className="flex h-full items-center justify-center text-gray-400">Cargando...</div>
);

const wrap = (el: JSX.Element) => <Suspense fallback={<Loading />}>{el}</Suspense>;

export const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: wrap(<LoginPage />) },
      { path: '/register', element: wrap(<RegisterParentPage />) },
      { path: '/forgot-password', element: wrap(<ForgotPasswordPage />) },
      { path: '/reset-password', element: wrap(<ResetPasswordPage />) },
    ],
  },
  {
    element: <DashboardLayout />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: '/dashboard', element: wrap(<DashboardPage />) },
      { path: '/institutions', element: wrap(<InstitutionsPage />) },
      { path: '/students', element: wrap(<StudentsPage />) },
      { path: '/parents', element: wrap(<ParentsPage />) },
      { path: '/transactions', element: wrap(<TransactionsPage />) },
      { path: '/my-wallet', element: wrap(<WalletPage />) },
      { path: '/my-payments', element: wrap(<MyPaymentsPage />) },
      { path: '/my-qr', element: wrap(<MyQRPage />) },
      { path: '/cafeterias', element: wrap(<CafeteriasPage />) },
      { path: '/cafeteria/dashboard', element: wrap(<CafeteriaDashboardPage />) },
      { path: '/cafeteria', element: wrap(<CafeteriaOperatorPage />) },
      { path: '/cafeteria/menu', element: wrap(<CafeteriaMenuPage />) },
      { path: '/cafeteria/inventario', element: wrap(<CafeteriaInventoryPage />) },
      { path: '/cafeteria/ventas', element: wrap(<CafeteriaVentasPage />) },
      { path: '/admin-payments', element: wrap(<AdminPaymentsPage />) },
      { path: '/reports', element: wrap(<ReportsPage />) },
      { path: '/staff', element: wrap(<StaffPage />) },
      { path: '/transport', element: wrap(<TransportPage />) },
      { path: '/transport/driver', element: wrap(<TransportDriverPage />) },
      { path: '/access', element: wrap(<AccessPage />) },
      { path: '/access/guard', element: wrap(<AccessGuardPage />) },
      { path: '/biometric-devices', element: wrap(<BiometricDevicesPage />) },
      { path: '/institution-admins', element: wrap(<InstitutionAdminsPage />) },
      { path: '/pre-orders', element: wrap(<ParentPreOrdersPage />) },
      { path: '/my-groups', element: wrap(<MyGroupsPage />) },
      { path: '/cafeteria/pre-orders', element: wrap(<KitchenManifestPage />) },
      { path: '/pre-orders-admin', element: wrap(<KitchenManifestPage />) },
      { path: '/teacher-groups', element: wrap(<TeacherGroupsAdminPage />) },
      { path: '/settings', element: wrap(<SettingsPage />) },
    ],
  },
  { path: '*', element: <Navigate to="/login" replace /> },
]);
